import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaEnvelope } from "react-icons/fa";
import { User } from "lucide-react";

// Skeleton avatar component — consistent across all cards
const SkeletonAvatar = ({ size = "lg" }) => {
  const dim = size === "lg" ? "w-36 h-36" : "w-24 h-24";
  const icon = size === "lg" ? "w-16 h-16" : "w-10 h-10";
  return (
    <div className={`${dim} rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center border-4 border-white shadow-xl`}>
      <User className={`${icon} text-primary/40`} strokeWidth={1} />
    </div>
  );
};

function Team() {
  // Leadership Team
  const leadership = [
    {
      id: 1,
      name: "Sudeesh Pabbathi",
      role: "CEO, USA",
      description: "With 22 years of experience, Sudeesh brings deep expertise in restaurant operations and strategic business transformation. Based in the USA, he leads Terces Solutions with a vision to build scalable, profitable food businesses.",
      email: "spabbathi@terces.io"
    },
    {
      id: 2,
      name: "Manoj Thammu",
      role: "Business Development Manager, USA",
      description: "A seasoned professional with 20 years of experience, Manoj drives business growth and client partnerships from the USA, helping Terces expand its footprint across international markets.",
      email: "mthammu@terces.io"
    },
    {
      id: 3,
      name: "Anvesh Thammu",
      role: "Service Delivery Manager, India",
      description: "With 4 years of dedicated experience, Anvesh manages client relationships and ensures seamless service delivery across all consulting engagements in India.",
      email: "athammu@terces.io"
    }
  ];

  // Extended Team
  const teamMembers = [
    {
      id: 1,
      name: "Sampath Alakanti",
      role: "Full Stack Engineer & Gen AI Specialist",
      description: "Architecting the digital realm",
      linkedin: "",
      email: ""
    },
    {
      id: 2,
      name: "Bhavana Lanke",
      role: "Restaurant Operations Manager",
      description: "Streamlining operations for peak performance",
      linkedin: "",
      email: ""
    },
    {
      id: 3,
      name: "Pavani Garnepally",
      role: "Data Analyst",
      description: "Turning data into actionable insights",
      linkedin: "",
      email: ""
    },
    {
      id: 4,
      name: "Dinesh Adapa",
      role: "Gen AI Expert & Digital Marketing Manager",
      description: "Driving growth through AI and digital innovation",
      linkedin: "",
      email: ""
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 px-6 overflow-hidden bg-[#F6F4EF]">
        {/* Decorative Elements */}
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" 
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-8"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 backdrop-blur-sm rounded-full border border-primary/30 shadow-lg">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span className="text-sm font-bold text-primary uppercase tracking-widest">
                  Our Team
                </span>
              </div>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Meet the Experts Behind{" "}
              <span className="block text-primary mt-2 relative">
                Terces Solutions
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1, delay: 0.8 }}
                  className="absolute bottom-0 left-0 h-1 bg-primary/20 rounded-full"
                />
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto"
            >
              A dedicated team of industry veterans committed to transforming restaurant operations through innovation and expertise
            </motion.p>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 bg-primary/10 rounded-full mb-6 relative pb-3">
              <span className="text-sm font-bold text-primary uppercase tracking-widest">
                Leadership
              </span>
              <div className="absolute bottom-1 left-6 right-6 h-0.5 bg-primary"></div>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Guiding Vision & Strategy
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Decades of combined experience driving restaurant excellence
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {leadership.map((member) => (
              <motion.div
                key={member.id}
                variants={cardVariants}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="group h-full"
              >
                <div className="bg-cream rounded-2xl overflow-hidden border border-gray-200 hover:border-primary transition-all duration-300 hover:shadow-2xl h-full flex flex-col">
                  {/* Image Section */}
                  <div className="relative bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center h-56">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <SkeletonAvatar size="lg" />
                    </motion.div>
                    <div className="absolute top-4 right-4 w-20 h-20 bg-primary/10 rounded-full blur-2xl" />
                    <div className="absolute bottom-4 left-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
                  </div>

                  {/* Content Section */}
                  <div className="p-8 flex-1 flex flex-col">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors duration-300">
                      {member.name}
                    </h3>
                    <div className="inline-block py-1.5 bg-primary/10 rounded-full mb-4">
                      <p className="text-sm font-semibold text-primary uppercase tracking-wide">
                        {member.role}
                      </p>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {member.description}
                    </p>

                    {/* Social Links */}
                    <div className="flex gap-3">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          aria-label={`Email ${member.name} at ${member.email}`}
                          title={member.email}
                          className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                        >
                          <FaEnvelope size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-20 px-6 bg-[#F6F4EF]">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6 relative pb-3 border border-primary/20">
              <span className="text-sm font-bold text-primary uppercase tracking-widest">
                Our Team
              </span>
              <div className="absolute bottom-1 left-6 right-6 h-0.5 bg-primary"></div>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              The People Making It Happen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Talented professionals bringing diverse expertise to every project
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
          >
            {teamMembers.map((member) => (
              <motion.div
                key={member.id}
                variants={cardVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group"
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-primary transition-all duration-300 hover:shadow-xl h-full flex flex-col">
                  {/* Image Section */}
                  <div className="relative bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center h-44">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <SkeletonAvatar size="sm" />
                    </motion.div>
                  </div>

                  {/* Content Section */}
                  <div className="p-4 text-center">
                    <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">
                      {member.role}
                    </p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {member.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="relative py-24 px-6 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-6 py-2 bg-primary/10 rounded-full mb-6 relative pb-3">
                <span className="text-sm font-bold text-primary uppercase tracking-widest">
                  Careers
                </span>
                <div className="absolute bottom-1 left-6 right-6 h-0.5 bg-primary"></div>
              </div>

              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Join Our Growing Team
              </h2>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                We're always looking for passionate individuals who want to make a real impact in the restaurant industry. If you're driven by innovation, collaboration, and excellence, we want to hear from you.
              </p>

              {/* Benefits */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl">
                    🚀
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Growth Opportunities</h3>
                    <p className="text-gray-600">Continuous learning and career advancement in a dynamic environment</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl">
                    💼
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Impactful Work</h3>
                    <p className="text-gray-600">Work on meaningful projects that transform restaurant businesses</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl">
                    🤝
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Collaborative Culture</h3>
                    <p className="text-gray-600">Work alongside talented professionals in a supportive team environment</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/Careers"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-white rounded-full font-bold text-lg hover:bg-primary/90 hover:scale-105 transition-all duration-300 shadow-xl"
                >
                  <span>Apply Now</span>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
                
                <a
                  href="mailto:admin@terces.io"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-cream text-primary rounded-full font-bold text-lg border-2 border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300"
                >
                  <span>Send Your CV</span>
                </a>
              </div>
            </motion.div>

            {/* Right Image/Stats */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-12 border border-primary/20">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
                    <div className="text-4xl font-bold text-primary mb-2">10+</div>
                    <div className="text-sm text-gray-600 uppercase tracking-wide">Team Members</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
                    <div className="text-4xl font-bold text-primary mb-2">15+</div>
                    <div className="text-sm text-gray-600 uppercase tracking-wide">Projects Delivered</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
                    <div className="text-4xl font-bold text-primary mb-2">100%</div>
                    <div className="text-sm text-gray-600 uppercase tracking-wide">Remote-Friendly</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
                    <div className="text-4xl font-bold text-primary mb-2">4+</div>
                    <div className="text-sm text-gray-600 uppercase tracking-wide">Years of Excellence</div>
                  </div>
                </div>

                {/* Quote */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="text-4xl text-primary mb-4">"</div>
                  <p className="text-gray-700 italic leading-relaxed mb-4">
                    At Terces, we believe in nurturing talent and creating an environment where everyone can thrive and make a difference.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-bold text-sm">TS</span>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm">Terces Leadership</div>
                      <div className="text-xs text-gray-600">Founding Team</div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Team;